// src/GraphAnimator.js
import React, { useRef, useEffect, useMemo } from 'react';
import * as THREE from 'three';

/**
 * Build a directed tournament graph with parity orientation.
 * Matches the pygame version's build_parity_graph function.
 */
const buildParityGraph = (n, rule = 'standard', k = 0) => {
  const edges = [];
  
  const orient = (i, j) => {
    // i < j always
    const same = (i % 2) === (j % 2);
    const succession = (i === j - 1);
    
    if (rule === 'zipper') {
      if (same) return [i, j];
      if (i % 2 === 0) return [i, j]; // even vs odd
      return (j - i) <= 2 * k ? [j, i] : [i, j];
    }
    
    // standard rule
    return (same || succession) ? [i, j] : [j, i];
  };
  
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      edges.push(orient(i, j));
    }
  }
  
  return { nodes: n, edges };
};

/**
 * Calculate layout positions for nodes.
 */
const calculateLayout = (n, layout = 'circle') => {
  const positions = {};
  
  if (layout === 'circle') {
    for (let i = 0; i < n; i++) {
      const angle = (2 * Math.PI * i) / n - Math.PI / 2;
      positions[i] = {
        x: (Math.cos(angle) + 1) / 2,
        y: (Math.sin(angle) + 1) / 2,
      };
    }
  } else {
    // Spring layout approximation using force-directed placement
    // Initialize randomly
    for (let i = 0; i < n; i++) {
      positions[i] = {
        x: 0.2 + Math.random() * 0.6,
        y: 0.2 + Math.random() * 0.6,
      };
    }
  }
  
  return positions;
};

/**
 * Particle class matching pygame version
 */
class Particle {
  constructor(u, v, speedRange, color) {
    this.u = u;
    this.v = v;
    this.homeU = u;
    this.homeV = v;
    this.color = color;
    this.speedRange = speedRange;
    this.reset();
  }
  
  reset() {
    this.t = 0;
    const [lo, hi] = this.speedRange;
    this.speed = (lo === hi ? lo : lo + Math.random() * (hi - lo)) / 100;
  }
  
  update(dt) {
    this.t += this.speed * dt;
  }
  
  getPosition(positions) {
    const p1 = positions[this.u];
    const p2 = positions[this.v];
    // Smooth step interpolation: t * t * (3 - 2 * t)
    const smoothT = this.t * this.t * (3 - 2 * this.t);
    return {
      x: p1.x + (p2.x - p1.x) * smoothT,
      y: p1.y + (p2.y - p1.y) * smoothT,
    };
  }
}

/**
 * GraphAnimator - Three.js implementation of the pygame parity graph visualizer
 */
const GraphAnimator = ({
  nNodes = 121,
  rule = 'standard',
  k = 0,
  layout = 'circle',
  mode = 'pingpong', // 'pulse' or 'pingpong'
  palette = null,
  particleSpeed = [13, 13],
  showNodes = false,
  showEdges = false,
  showLabels = false,
  className = '',
  style = {},
}) => {
  const containerRef = useRef(null);
  const rendererRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const particlesRef = useRef([]);
  const positionsRef = useRef({});
  const modeRef = useRef(mode);
  const animationFrameRef = useRef(null);
  const lastTimeRef = useRef(performance.now());
  
  // Default palette matching the webapp theme
  const defaultPalette = useMemo(() => [
    new THREE.Color('#61f2ff'), // highlight-text cyan
    new THREE.Color('#f4b2ff'), // soft pink/magenta
    new THREE.Color('#e2e6f3'), // text-primary off-white
  ], []);
  
  const colors = useMemo(() => {
    if (palette) {
      return palette.map(c => {
        if (c instanceof THREE.Color) return c;
        if (typeof c === 'string') return new THREE.Color(c);
        if (Array.isArray(c)) {
          return new THREE.Color(c[0] / 255, c[1] / 255, c[2] / 255);
        }
        return new THREE.Color('#ffffff');
      });
    }
    return defaultPalette;
  }, [palette, defaultPalette]);
  
  // Update mode ref when prop changes
  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);
  
  // Initialize Three.js scene
  useEffect(() => {
    if (!containerRef.current) return;
    
    const container = containerRef.current;
    const width = container.clientWidth;
    const height = container.clientHeight;
    
    // Scene
    const scene = new THREE.Scene();
    scene.background = null; // Transparent
    sceneRef.current = scene;
    
    // Camera (orthographic for 2D)
    const camera = new THREE.OrthographicCamera(0, 1, 1, 0, 0.1, 10);
    camera.position.z = 1;
    cameraRef.current = camera;
    
    // Renderer
    const renderer = new THREE.WebGLRenderer({ 
      antialias: true, 
      alpha: true,
      powerPreference: 'high-performance',
    });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;
    
    // Build graph
    const graph = buildParityGraph(nNodes, rule, k);
    const positions = calculateLayout(nNodes, layout);
    positionsRef.current = positions;
    
    // Create node colors based on palette
    const nodeColors = {};
    const sortedNodes = Array.from({ length: nNodes }, (_, i) => i);
    sortedNodes.forEach((node, idx) => {
      nodeColors[node] = colors[idx % colors.length];
    });
    
    // Create particles for each edge
    const particles = graph.edges.map(([u, v]) => {
      const color = nodeColors[u] || colors[0];
      return new Particle(u, v, particleSpeed, color);
    });
    particlesRef.current = particles;
    
    // Create particle geometry
    const particleGeometry = new THREE.BufferGeometry();
    const particlePositions = new Float32Array(particles.length * 3);
    const particleColors = new Float32Array(particles.length * 3);
    
    particles.forEach((particle, i) => {
      const pos = particle.getPosition(positions);
      particlePositions[i * 3] = pos.x;
      particlePositions[i * 3 + 1] = pos.y;
      particlePositions[i * 3 + 2] = 0;
      
      particleColors[i * 3] = particle.color.r;
      particleColors[i * 3 + 1] = particle.color.g;
      particleColors[i * 3 + 2] = particle.color.b;
    });
    
    particleGeometry.setAttribute('position', new THREE.BufferAttribute(particlePositions, 3));
    particleGeometry.setAttribute('color', new THREE.BufferAttribute(particleColors, 3));
    
    // Create glow texture
    const createGlowTexture = () => {
      const canvas = document.createElement('canvas');
      canvas.width = 64;
      canvas.height = 64;
      const ctx = canvas.getContext('2d');
      
      // Radial gradient for soft glow
      const gradient = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
      gradient.addColorStop(0, 'rgba(255, 255, 255, 1)');
      gradient.addColorStop(0.2, 'rgba(255, 255, 255, 0.8)');
      gradient.addColorStop(0.4, 'rgba(255, 255, 255, 0.4)');
      gradient.addColorStop(0.7, 'rgba(255, 255, 255, 0.1)');
      gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
      
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, 64, 64);
      
      const texture = new THREE.CanvasTexture(canvas);
      return texture;
    };
    
    const glowTexture = createGlowTexture();
    
    // Particle material with glow effect
    const particleMaterial = new THREE.PointsMaterial({
      size: 3,
      map: glowTexture,
      vertexColors: true,
      transparent: true,
      opacity: 0.9,
      sizeAttenuation: false,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    
    const particlePoints = new THREE.Points(particleGeometry, particleMaterial);
    scene.add(particlePoints);
    
    // Optional: Add edges
    if (showEdges) {
      const edgeMaterial = new THREE.LineBasicMaterial({ 
        color: 0x2b1b3f,
        transparent: true,
        opacity: 0.3,
      });
      
      graph.edges.forEach(([u, v]) => {
        const p1 = positions[u];
        const p2 = positions[v];
        const geometry = new THREE.BufferGeometry().setFromPoints([
          new THREE.Vector3(p1.x, p1.y, 0),
          new THREE.Vector3(p2.x, p2.y, 0),
        ]);
        const line = new THREE.Line(geometry, edgeMaterial);
        scene.add(line);
      });
    }
    
    // Optional: Add nodes
    if (showNodes) {
      const nodeGeometry = new THREE.CircleGeometry(0.008, 16);
      sortedNodes.forEach(node => {
        const pos = positions[node];
        const material = new THREE.MeshBasicMaterial({ 
          color: nodeColors[node],
          transparent: true,
          opacity: 0.8,
        });
        const mesh = new THREE.Mesh(nodeGeometry, material);
        mesh.position.set(pos.x, pos.y, 0);
        scene.add(mesh);
      });
    }
    
    // Animation loop
    const animate = () => {
      const now = performance.now();
      const dt = (now - lastTimeRef.current) / 1000;
      lastTimeRef.current = now;
      
      const positions = positionsRef.current;
      const positionAttr = particleGeometry.attributes.position;
      
      particles.forEach((particle, i) => {
        particle.update(dt);
        
        if (particle.t >= 1.0) {
          if (modeRef.current === 'pulse') {
            particle.u = particle.homeU;
            particle.v = particle.homeV;
          } else {
            // pingpong: swap direction
            const temp = particle.u;
            particle.u = particle.v;
            particle.v = temp;
          }
          particle.reset();
        }
        
        const pos = particle.getPosition(positions);
        positionAttr.array[i * 3] = pos.x;
        positionAttr.array[i * 3 + 1] = pos.y;
      });
      
      positionAttr.needsUpdate = true;
      renderer.render(scene, camera);
      animationFrameRef.current = requestAnimationFrame(animate);
    };
    
    animate();
    
    // Handle resize
    const handleResize = () => {
      if (!containerRef.current || !rendererRef.current) return;
      const w = containerRef.current.clientWidth;
      const h = containerRef.current.clientHeight;
      rendererRef.current.setSize(w, h);
    };
    
    window.addEventListener('resize', handleResize);
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (rendererRef.current && container.contains(rendererRef.current.domElement)) {
        container.removeChild(rendererRef.current.domElement);
      }
      if (rendererRef.current) {
        rendererRef.current.dispose();
      }
    };
  }, [nNodes, rule, k, layout, colors, particleSpeed, showNodes, showEdges]);
  
  return (
    <div
      ref={containerRef}
      className={`graph-animator ${className}`}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        ...style,
      }}
    />
  );
};

export default GraphAnimator;